import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import AllocateTable from './AllocateTable';
import AllocationStrip from './AllocationStrip';
import { allParentKeys, computeFigures, mergeAllocations, overCountAfter, visibleRows, withoutColumn } from './calculations';
import CellEditor from './CellEditor';
import ColumnModal from './ColumnModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import costAllocationApi, { errorMessage } from './costAllocationApi';
import CreateReportModal from './CreateReportModal';
import NotePanel from './NotePanel';
import RenameReportModal from './RenameReportModal';
import ReportHeader, { VIEW_ALLOCATE, VIEW_SUMMARY } from './ReportHeader';
import ReportTabsBar from './ReportTabsBar';
import SummaryTable from './SummaryTable';
import TableSkeleton from './TableSkeleton';

import Button from 'CommonComponents/Button';
import EmptyState from 'CommonComponents/EmptyState';

import { useToast } from 'Hooks/useToast';

import ChartTableIcon from 'Assets/icons/chart-table.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const EMPTY_DATA = { allocations: {}, lineNotes: {} };

/** Paint the skeleton before a heavy table render, then show the table. */
const afterPaint = (callback) => window.requestAnimationFrame(() => window.setTimeout(callback, 0));

const saveBlob = (response, fallbackName) => {
    const disposition = response.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const name = match ? decodeURIComponent(match[1]) : fallbackName;
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Cost Allocation: report tabs, and for the active report the Allocate and Summary
 * views of the budget lines. Every change is merged from the server's response into
 * what is on screen; the page never reloads.
 */
export default function CostAllocationIndex({ indexUrl, reportsUrl, abilities }) {
    const api = useMemo(() => costAllocationApi(indexUrl, reportsUrl), [indexUrl, reportsUrl]);
    const toast = useToast();

    const [reports, setReports] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [activeReportId, setActiveReportId] = useState(null);
    const [view, setView] = useState(VIEW_ALLOCATE);
    const [switchingView, setSwitchingView] = useState(false);
    const [reportData, setReportData] = useState({});
    const [failedReports, setFailedReports] = useState(() => new Set());
    const [expanded, setExpanded] = useState(() => new Set());
    const [exporting, setExporting] = useState(false);
    const [editing, setEditing] = useState(null);
    const [savingByRow, setSavingByRow] = useState({});
    const [dialog, setDialog] = useState(null);
    const [deleteCount, setDeleteCount] = useState(null);
    const [note, setNote] = useState(null);
    const loadingReports = useRef(new Set());

    const activeReport = reports.find((report) => report.id === activeReportId) || null;
    const activeData = activeReportId ? reportData[activeReportId] : null;
    const reportFailed = !!activeReport && !activeData && failedReports.has(activeReportId);
    const reportLoading = !!activeReport && !activeData && !reportFailed;
    // While the active report has failed to load, nothing in it can be written until it
    // loads; a new report can still be created.
    const writeAbilities = useMemo(() => (reportFailed ? { ...abilities, create: false, update: false, delete: false } : abilities), [abilities, reportFailed]);
    const loading = pageLoading || reportLoading || switchingView;

    const loadReport = useCallback(
        (reportId) => {
            if (loadingReports.current.has(reportId)) {
                return;
            }

            loadingReports.current.add(reportId);
            api.showReport(reportId)
                .then((data) => setReportData((prev) => ({ ...prev, [reportId]: { allocations: data.allocations || {}, lineNotes: data.lineNotes || {} } })))
                .catch((error) => {
                    setFailedReports((prev) => new Set(prev).add(reportId));
                    toast.error(errorMessage(error, $t('The report could not be loaded.')));
                })
                .finally(() => loadingReports.current.delete(reportId));
        },
        [api, toast],
    );

    useEffect(() => {
        api.index()
            .then((data) => {
                setReports(data.reports || []);
                setDepartments(data.lines || []);
                setActiveReportId(data.reports?.[0]?.id ?? null);
            })
            .catch((error) => {
                setLoadFailed(true);
                toast.error(errorMessage(error, $t('Cost allocation could not be loaded.')));
            })
            .finally(() => setPageLoading(false));
    }, [api, toast]);

    useEffect(() => {
        if (activeReportId && !reportData[activeReportId] && !failedReports.has(activeReportId)) {
            loadReport(activeReportId);
        }
    }, [activeReportId, reportData, failedReports, loadReport]);

    const retryReport = () =>
        setFailedReports((prev) => {
            const next = new Set(prev);
            next.delete(activeReportId);

            return next;
        });

    const columns = activeReport?.columns || [];
    const allocations = activeData?.allocations || EMPTY_DATA.allocations;
    const lineNotes = activeData?.lineNotes || EMPTY_DATA.lineNotes;

    const figures = useMemo(() => computeFigures(departments, columns, allocations), [departments, columns, allocations]);
    const rows = useMemo(() => visibleRows(departments, expanded), [departments, expanded]);

    // Report and data mutations ───────────────────────────────────────────

    const updateReport = (reportId, change) => setReports((prev) => prev.map((report) => (report.id === reportId ? { ...report, ...change(report) } : report)));
    const updateData = (reportId, change) =>
        setReportData((prev) => ({ ...prev, [reportId]: { ...EMPTY_DATA, ...prev[reportId], ...change(prev[reportId] || EMPTY_DATA) } }));

    const openReport = (report) => {
        setReports((prev) => [...prev, { id: report.id, title: report.title, columns: report.columns }]);
        setReportData((prev) => ({ ...prev, [report.id]: { allocations: report.allocations || {}, lineNotes: report.lineNotes || {} } }));
        setActiveReportId(report.id);
        setView(VIEW_ALLOCATE);
    };

    const selectReport = (reportId) => {
        if (reportId === activeReportId) {
            return;
        }

        setEditing(null);
        setActiveReportId(reportId);
        setView(VIEW_ALLOCATE);

        if (reportData[reportId]) {
            setSwitchingView(true);
            afterPaint(() => setSwitchingView(false));
        }
    };

    const changeView = (next) => {
        if (next === view) {
            return;
        }

        setEditing(null);
        setSwitchingView(true);
        setView(next);
        afterPaint(() => setSwitchingView(false));
    };

    const toggleRow = useCallback((key) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);

            return next;
        });
    }, []);

    const closeDialog = useCallback(() => setDialog(null), []);

    const createReport = async (payload) => {
        const report = await api.createReport(payload);
        setDialog(null);
        openReport(report);
        toast.success($t('Report {{title}} created', { title: report.title }));
    };

    const renameReport = async (title) => {
        const report = await api.renameReport(activeReportId, title);
        updateReport(report.id, () => ({ title: report.title }));
        setDialog(null);
        toast.success($t('Report renamed'));
    };

    const duplicateReport = async () => {
        try {
            const report = await api.duplicateReport(activeReportId);
            openReport(report);
            toast.success($t('Report {{title}} created', { title: report.title }));
        } catch (error) {
            toast.error(errorMessage(error, $t('The report could not be duplicated.')));
        }
    };

    const deleteReport = async () => {
        try {
            await api.deleteReport(activeReportId);
            const remaining = reports.filter((report) => report.id !== activeReportId);
            setReports(remaining);
            setActiveReportId(remaining[0]?.id ?? null);
            setView(VIEW_ALLOCATE);
            setDialog(null);
            toast.success($t('Report deleted'));
        } catch (error) {
            toast.error(errorMessage(error, $t('The report could not be deleted.')));
        }
    };

    const saveColumn = async (payload) => {
        const reportId = activeReportId;
        const column = dialog.column;

        if (column) {
            const saved = await api.updateColumn(reportId, column.id, payload);
            updateReport(reportId, (report) => ({ columns: report.columns.map((existing) => (existing.id === saved.id ? saved : existing)) }));
            toast.success($t('Column saved'));
        } else {
            const result = await api.createColumn(reportId, payload);
            updateReport(reportId, (report) => ({ columns: [...report.columns, result.column] }));
            updateData(reportId, (data) => ({ allocations: mergeAllocations(data.allocations, result.column.id, result.allocations) }));
            toast.success($t('Column {{name}} added', { name: result.column.name }));
        }

        setDialog(null);
    };

    const askDeleteColumn = (column) => {
        setDeleteCount(null);
        setDialog({ type: 'deleteColumn', column });
        api.allocationCount(activeReportId, column.id)
            .then(setDeleteCount)
            .catch(() => setDeleteCount(0));
    };

    const deleteColumn = async () => {
        const reportId = activeReportId;
        const column = dialog.column;

        try {
            await api.deleteColumn(reportId, column.id);
            updateReport(reportId, (report) => ({ columns: report.columns.filter((existing) => existing.id !== column.id) }));
            updateData(reportId, (data) => ({ allocations: withoutColumn(data.allocations, column.id) }));
            setDialog(null);
            toast.success($t('Column {{name}} deleted', { name: column.name }));
        } catch (error) {
            toast.error(errorMessage(error, $t('The column could not be deleted.')));
        }
    };

    const saveNote = async (text) => {
        const reportId = activeReportId;

        try {
            if (note.kind === 'column') {
                const column = columns.find((existing) => existing.id === note.columnId);
                const saved = await api.updateColumn(reportId, column.id, { name: column.name, note: text });
                updateReport(reportId, (report) => ({ columns: report.columns.map((existing) => (existing.id === saved.id ? saved : existing)) }));
            } else {
                const saved = await api.updateLineNote(reportId, note.key, text);
                updateData(reportId, (data) => {
                    const next = { ...data.lineNotes };
                    saved.note ? (next[note.key] = saved.note) : delete next[note.key];

                    return { lineNotes: next };
                });
            }

            setNote(null);
            toast.success(text ? $t('Note saved') : $t('Note removed'));
        } catch (error) {
            toast.error(errorMessage(error, $t('The note could not be saved.')));
            throw error;
        }
    };

    // Cell editing ─────────────────────────────────────────────────────────

    const editCell = useCallback((row, column, anchor) => setEditing({ rowKey: row.key, row, columnId: column.id, anchor }), []);
    const cancelEdit = useCallback(() => setEditing((current) => (current?.saving ? current : null)), []);

    /** The editor for the same row in the next column, or null after the last column. */
    const nextEditing = ({ row, columnId, anchor }) => {
        const index = columns.findIndex((column) => column.id === columnId);
        const nextColumn = columns[index + 1];
        const nextAnchor = nextColumn ? anchor.closest('td')?.nextElementSibling?.querySelector('[role="button"]') : null;

        return nextColumn && nextAnchor ? { rowKey: row.key, row, columnId: nextColumn.id, anchor: nextAnchor } : null;
    };

    const skipCell = () => setEditing(nextEditing(editing));

    const saveCell = async (pct, moveNext) => {
        const current = editing;
        const { row, columnId } = current;
        const reportId = activeReportId;
        const payload =
            row.kind === 'line'
                ? { scope: 'line', expense_id: row.line.id, pct }
                : row.kind === 'project'
                  ? { scope: 'project', expense_id: row.line.id, pct }
                  : row.kind === 'costcenter'
                    ? { scope: 'costcenter', department_id: row.department.id, costcenter_id: row.costcenter.id, pct }
                    : { scope: 'department', department_id: row.department.id, pct };

        setEditing((current) => ({ ...current, saving: true }));
        setSavingByRow((prev) => ({ ...prev, [row.key]: new Set([...(prev[row.key] || []), columnId]) }));

        try {
            const changes = await api.updateAllocations(reportId, columnId, payload);
            updateData(reportId, (data) => ({ allocations: mergeAllocations(data.allocations, columnId, changes) }));
            setEditing(moveNext ? nextEditing(current) : null);
        } catch (error) {
            toast.error(errorMessage(error, $t('The allocation could not be saved.')));
            setEditing((current) => (current ? { ...current, saving: false } : current));
        } finally {
            setSavingByRow((prev) => {
                const next = new Set(prev[row.key] || []);
                next.delete(columnId);

                return { ...prev, [row.key]: next };
            });
        }
    };

    const exportReport = async () => {
        setExporting(true);

        try {
            const response = await api.exportReport(activeReportId);
            saveBlob(response, `${activeReport.title}.xlsx`);
            toast.success($t('{{title}} exported to Excel', { title: activeReport.title }));
        } catch (error) {
            toast.error($t('The file could not be prepared.'));
        } finally {
            setExporting(false);
        }
    };

    // Render ───────────────────────────────────────────────────────────────

    const openNote = (row) => setNote({ kind: 'line', key: row.noteKey, label: row.path, note: lineNotes[row.noteKey] || '' });
    const openColumnNote = (column) => setNote({ kind: 'column', columnId: column.id, label: column.name, note: column.note || '' });

    const statusText = () => {
        if (pageLoading) {
            return $t('Loading cost allocation…');
        }

        return view === VIEW_SUMMARY
            ? $t('Loading {{title}} summary…', { title: activeReport.title })
            : $t('Loading {{title}} report…', { title: activeReport.title });
    };

    const renderBody = () => {
        if (loading) {
            return <TableSkeleton status={statusText()} columns={Math.min(columns.length + 3, 8)} />;
        }

        if (reportFailed) {
            return (
                <EmptyState
                    className="tw-py-12"
                    heading={$t('The report could not be loaded.')}
                    body={$t('Check your connection and try again.')}
                    action={
                        <Button variant="outline" small onClick={retryReport}>
                            {$t('Try again')}
                        </Button>
                    }
                />
            );
        }

        if (departments.length === 0) {
            return (
                <EmptyState
                    className="tw-py-12"
                    heading={$t('No budget lines to allocate')}
                    body={$t('Lines added to the budget appear here, grouped by department and cost centre.')}
                />
            );
        }

        return (
            <>
                <AllocationStrip total={figures.total} />
                {view === VIEW_ALLOCATE ? (
                    <AllocateTable
                        rows={rows}
                        figures={figures}
                        columns={columns}
                        expanded={expanded}
                        onToggle={toggleRow}
                        canEdit={writeAbilities.update}
                        editing={editing}
                        savingByRow={savingByRow}
                        lineNotes={lineNotes}
                        onEditCell={editCell}
                        onNote={openNote}
                        onColumnNote={openColumnNote}
                        onRenameColumn={(column) => setDialog({ type: 'column', column })}
                        onDeleteColumn={askDeleteColumn}
                        onAddColumn={() => setDialog({ type: 'column', column: null })}
                    />
                ) : (
                    <SummaryTable rows={rows} figures={figures} columns={columns} expanded={expanded} onToggle={toggleRow} />
                )}
            </>
        );
    };

    const editingColumn = editing ? columns.find((column) => column.id === editing.columnId) : null;

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-3.5">
            {!pageLoading && !loadFailed && (
                <ReportTabsBar
                    reports={reports}
                    activeReportId={activeReportId}
                    onSelect={selectReport}
                    onCreate={() => setDialog({ type: 'create' })}
                    canCreate={abilities.create}
                    canExport={abilities.export}
                    onExport={exportReport}
                    exporting={exporting}
                    exportDisabled={loading || reportFailed || !activeReport}
                />
            )}

            <div className="tw-rounded-lg tw-bg-white tw-shadow-primary">
                {pageLoading && <TableSkeleton status={statusText()} />}
                {!pageLoading && !activeReport && !loadFailed && (
                    <EmptyState
                        className="tw-py-12"
                        icon={ChartTableIcon}
                        heading={$t('No cost allocation reports yet')}
                        body={$t('Create a report to allocate the budget across funding sources, cost types, venues or any split you need.')}
                        action={
                            abilities.create && (
                                <Button variant="primary" onClick={() => setDialog({ type: 'create' })}>
                                    <PlusIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                                    {$t('New report')}
                                </Button>
                            )
                        }
                    />
                )}
                {!pageLoading && activeReport && (
                    <>
                        <ReportHeader
                            report={activeReport}
                            view={view}
                            onViewChange={changeView}
                            onExpandAll={() => setExpanded(new Set(allParentKeys(departments)))}
                            onCollapseAll={() => setExpanded(new Set())}
                            onAddColumn={() => setDialog({ type: 'column', column: null })}
                            onRename={() => setDialog({ type: 'rename' })}
                            onDuplicate={duplicateReport}
                            onDelete={() => setDialog({ type: 'deleteReport' })}
                            abilities={writeAbilities}
                            busy={loading}
                        />
                        {renderBody()}
                    </>
                )}
            </div>

            {editing && editingColumn && figures[editing.rowKey] && (
                <CellEditor
                    key={`${editing.rowKey}-${editing.columnId}`}
                    anchor={editing.anchor}
                    row={editing.row}
                    column={editingColumn}
                    figures={figures[editing.rowKey]}
                    saving={!!editing.saving}
                    onSave={saveCell}
                    onCancel={cancelEdit}
                    onSkip={skipCell}
                    countOverAfter={(pct) => overCountAfter(editing.row, columns, allocations, editing.columnId, pct)}
                />
            )}

            <CreateReportModal isOpen={dialog?.type === 'create'} onClose={closeDialog} reports={reports} onCreate={createReport} />
            <RenameReportModal isOpen={dialog?.type === 'rename'} onClose={closeDialog} report={activeReport} reports={reports} onSave={renameReport} />
            <ColumnModal isOpen={dialog?.type === 'column'} onClose={closeDialog} column={dialog?.column ?? null} columns={columns} onSave={saveColumn} />
            <ConfirmDeleteModal
                id="cost-allocation-delete-column"
                isOpen={dialog?.type === 'deleteColumn'}
                onClose={closeDialog}
                loading={deleteCount === null}
                title={$t('Delete column {{name}}?', { name: dialog?.column?.name ?? '' })}
                keepLabel={$t('Keep column')}
                deleteLabel={$t('Delete column')}
                onConfirm={deleteColumn}
            >
                <p className="tw-m-0">
                    {deleteCount === 0
                        ? $t('No allocations have been entered against this column.')
                        : deleteCount === 1
                          ? $t('1 allocation entered against this column will be removed.')
                          : $t('{{count}} allocations entered against this column will be removed.', { count: deleteCount })}
                </p>
                <p className="tw-m-0">{$t('Line notes are kept. This cannot be undone.')}</p>
            </ConfirmDeleteModal>
            <ConfirmDeleteModal
                id="cost-allocation-delete-report"
                isOpen={dialog?.type === 'deleteReport'}
                onClose={closeDialog}
                title={$t('Delete {{title}}?', { title: activeReport?.title ?? '' })}
                keepLabel={$t('Keep report')}
                deleteLabel={$t('Delete report')}
                onConfirm={deleteReport}
            >
                <p className="tw-m-0">
                    {$t('All {{count}} columns, their allocations and notes in this report will be removed. The budget itself is not affected.', {
                        count: columns.length,
                    })}
                </p>
                <p className="tw-m-0">{$t('This cannot be undone.')}</p>
            </ConfirmDeleteModal>

            {note && (
                <NotePanel
                    key={note.kind === 'column' ? `column-${note.columnId}` : note.key}
                    target={note}
                    reportTitle={activeReport?.title ?? ''}
                    canEdit={writeAbilities.update}
                    onSave={saveNote}
                    onClose={() => setNote(null)}
                />
            )}
        </div>
    );
}

const rootEl = document.getElementById('costAllocation');
if (rootEl) {
    ReactDOM.render(
        <App>
            <CostAllocationIndex
                indexUrl={rootEl.getAttribute('data-index-url')}
                reportsUrl={rootEl.getAttribute('data-reports-url')}
                abilities={JSON.parse(rootEl.getAttribute('data-abilities') || '{}')}
            />
        </App>,
        rootEl,
    );
}
